import usePrompts from '../../api/prompts/usePrompts.ts';
import FormBlock from '../../components/form/FormBlock.tsx';
import FormButtons from 'components/form/FormButtons';
import { getFormSchema, getUiSchema, validator } from '../../api/prompts/formSchema.ts';
import { folderExtraErrors, getFolderPathServerError } from 'utils/folderPath';
import { useState } from 'react';
import { CreatePromptRequest } from '../../api/model.ts';
import { toast } from 'react-toastify';
import useProjects from 'api/projects/useProjects.ts';
import Loader from 'components/Loader.tsx';
import { SettingsFormShell } from 'components/settingsHub/SettingsFormShell';

const FORM_ID = 'prompt-add-form';

type PromptAddProps = {
  projectId: string;
  requiredInputs?: string[];
  additionalInputsAllowed?: boolean;
  templateHint?: string;
  onCancel: () => void;
  onSubmit: (p: string) => void;
};

const PromptAdd = ({
  projectId,
  requiredInputs,
  additionalInputsAllowed = true,
  templateHint,
  onSubmit,
  onCancel,
}: PromptAddProps) => {
  const {
    create: { isPending, mutateAsync },
  } = usePrompts(projectId);
  const { projects, isLoading: areProjectsLoading } = useProjects();

  const initialPrompt = requiredInputs ? ({ inputs: requiredInputs } as CreatePromptRequest) : undefined;

  const [fieldsInputs, setFieldsInputs] = useState<string[]>([]);
  const [folderError, setFolderError] = useState<string | null>(null);

  const handleSubmit = async (formData: CreatePromptRequest) => {
    if (requiredInputs?.some((input) => !formData.inputs.includes(input))) {
      toast.error(`Prompt requires the following input fields: ${requiredInputs.join(', ')}`);
      return;
    }
    setFolderError(null);
    try {
      const { id: createdPromptId } = await mutateAsync(formData);
      onSubmit(createdPromptId);
    } catch (error) {
      setFolderError(getFolderPathServerError(error));
    }
  };

  const handleOnChange = (formData: CreatePromptRequest) => {
    setFieldsInputs(formData.inputs);
  };

  if (areProjectsLoading) {
    return <Loader />;
  }

  return (
    <SettingsFormShell
      title="Add Prompt"
      footer={<FormButtons formId={FORM_ID} onCancel={onCancel} isLoading={isPending} />}
    >
      <FormBlock
        onSubmit={(formData) => void handleSubmit(formData)}
        onCancel={onCancel}
        onChange={handleOnChange}
        schema={getFormSchema(projects, projectId, fieldsInputs, templateHint)}
        initialValues={initialPrompt}
        uiSchema={getUiSchema(fieldsInputs, requiredInputs, additionalInputsAllowed)}
        isLoading={isPending}
        formId={FORM_ID}
        validator={validator}
        extraErrors={folderExtraErrors(folderError)}
        showButtons={false}
      ></FormBlock>
    </SettingsFormShell>
  );
};

export default PromptAdd;
